/**
 * The icon set. Hand-authored SVG on a 24×24 grid, styled after the Nintendo Switch system UI.
 *
 * This exists because the UI previously drew its icons with text: `⏸ ‹ › ☰ ⛶` and the emoji `🔇 🔊`.
 * That is the single biggest source of "this looks cheap":
 *
 *   - `🔇` and `🔊` are emoji. iOS renders them as full-colour Apple artwork that ignores the
 *     surrounding colour entirely, so a "monochrome control bar" had two cartoon speakers in it.
 *   - `‹ › ☰ ⛶` are ordinary text. Their weight, size and vertical centring come from whatever font
 *     happens to resolve, which differs across iOS, Android and Windows — buttons that should be
 *     identical came out visibly mismatched.
 *   - None of them can inherit a stroke width or align to a shared optical grid.
 *
 * SVG fixes all three: one geometry, `currentColor`, and identical metrics everywhere.
 *
 * Two deliberate styles, mirroring how a console UI actually works:
 *   - transport controls (play/pause/prev/next/volume) are SOLID, so they read instantly at a glance
 *     from across a room, which is the whole point of a photo frame;
 *   - everything else is a 2px round-capped STROKE, which stays legible at 16px.
 *
 * The `<svg>` wrapper supplies `fill="none" stroke="currentColor"`, so a solid shape only has to say
 * `fill="currentColor"` on its own path.
 *
 * Exported as markup strings rather than components because two of the four entry points (`/join`,
 * `/debug`) are plain TypeScript with no renderer. `Icon.tsx` wraps this for the Preact pages.
 */

export type IconName =
  | 'chevronLeft'
  | 'chevronRight'
  | 'play'
  | 'pause'
  | 'prev'
  | 'next'
  | 'volumeOn'
  | 'volumeOff'
  | 'expand'
  | 'collapse'
  | 'download'
  | 'openExternal'
  | 'list'
  | 'image'
  | 'video'
  | 'trash'
  | 'plus'
  | 'check'
  | 'close'
  | 'sun'
  | 'moon'
  | 'user'
  | 'users'
  | 'device'
  | 'album'
  | 'key'
  | 'settings'
  | 'clock'
  | 'upload'
  | 'grid'
  | 'shield'
  | 'refresh'
  | 'alert'
  | 'info'
  | 'home'
  | 'sparkle';

const ICONS: Record<IconName, string> = {
  chevronLeft: '<path d="M14.5 5.5 8 12l6.5 6.5"/>',
  chevronRight: '<path d="M9.5 5.5 16 12l-6.5 6.5"/>',

  // Transport: solid, because these are read at a glance from across the room.
  play: '<path d="M8.5 5.3 19.2 12 8.5 18.7Z" fill="currentColor"/>',
  pause: '<path d="M9.3 5.5v13M14.7 5.5v13" stroke-width="3.2"/>',
  prev: '<path d="M18 6.2 10.4 12 18 17.8Z" fill="currentColor"/><path d="M6.4 5.6v12.8"/>',
  next: '<path d="M6 6.2 13.6 12 6 17.8Z" fill="currentColor"/><path d="M17.6 5.6v12.8"/>',
  volumeOn:
    '<path d="M3.5 9.3h3.6L11.8 5.3v13.4L7.1 14.7H3.5Z" fill="currentColor"/><path d="M15.4 9.4a3.6 3.6 0 0 1 0 5.2M18.3 6.8a7.6 7.6 0 0 1 0 10.4"/>',
  volumeOff:
    '<path d="M3.5 9.3h3.6L11.8 5.3v13.4L7.1 14.7H3.5Z" fill="currentColor"/><path d="m15.6 9.6 5.2 4.8M20.8 9.6l-5.2 4.8"/>',

  expand: '<path d="M9.5 4.5h-5v5M14.5 4.5h5v5M14.5 19.5h5v-5M9.5 19.5h-5v-5"/>',
  collapse: '<path d="M4.5 9.5h5v-5M19.5 9.5h-5v-5M19.5 14.5h-5v5M4.5 14.5h5v5"/>',

  download: '<path d="M12 3.8v10.7M7.6 10.2 12 14.6l4.4-4.4M4.5 19.5h15"/>',
  openExternal: '<path d="M13.5 4.5h6v6M19.5 4.5 11 13M17 14v4.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 5 18.5v-9A1.5 1.5 0 0 1 6.5 8H11"/>',

  list: '<path d="M9 6.5h11M9 12h11M9 17.5h11"/><circle cx="4.6" cy="6.5" r="1.35" fill="currentColor" stroke="none"/><circle cx="4.6" cy="12" r="1.35" fill="currentColor" stroke="none"/><circle cx="4.6" cy="17.5" r="1.35" fill="currentColor" stroke="none"/>',
  grid: '<rect x="4" y="4" width="6.6" height="6.6" rx="1.8"/><rect x="13.4" y="4" width="6.6" height="6.6" rx="1.8"/><rect x="4" y="13.4" width="6.6" height="6.6" rx="1.8"/><rect x="13.4" y="13.4" width="6.6" height="6.6" rx="1.8"/>',

  image: '<rect x="3.5" y="5" width="17" height="14" rx="2.8"/><circle cx="9" cy="10" r="1.7"/><path d="m4.6 16.8 4.6-4.6 3.4 3.4 2.7-2.7 4.1 4.1"/>',
  video: '<rect x="3" y="6" width="12.6" height="12" rx="2.8"/><path d="m15.9 11.1 5.1-3.1v8l-5.1-3.1Z" fill="currentColor"/>',
  album: '<path d="M3.6 7.6a2.1 2.1 0 0 1 2.1-2.1h3.1l2 2.2h7.5a2.1 2.1 0 0 1 2.1 2.1v8.6a2.1 2.1 0 0 1-2.1 2.1H5.7a2.1 2.1 0 0 1-2.1-2.1Z"/>',

  trash: '<path d="M4.5 6.8h15M9.6 6.8V4.4h4.8v2.4M6.6 6.8 7.6 19.6h8.8L17.4 6.8"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  check: '<path d="m4.8 12.4 5 5 9.4-11"/>',
  close: '<path d="m6.2 6.2 11.6 11.6M17.8 6.2 6.2 17.8"/>',

  sun: '<circle cx="12" cy="12" r="4.1"/><path d="M12 2.6v2.1M12 19.3v2.1M4.3 4.3l1.5 1.5M18.2 18.2l1.5 1.5M2.6 12h2.1M19.3 12h2.1M4.3 19.7l1.5-1.5M18.2 5.8l1.5-1.5"/>',
  moon: '<path d="M20.2 14.7A8.6 8.6 0 0 1 9.3 3.8a8.6 8.6 0 1 0 10.9 10.9Z"/>',

  user: '<circle cx="12" cy="8.1" r="3.9"/><path d="M4.6 20.2a7.4 7.4 0 0 1 14.8 0"/>',
  users: '<circle cx="9.2" cy="8.4" r="3.5"/><path d="M2.8 19.8a6.4 6.4 0 0 1 12.8 0M16.2 5.3a3.5 3.5 0 0 1 0 6.2M17.6 14.2a6.4 6.4 0 0 1 3.6 5.6"/>',
  device: '<rect x="6" y="2.8" width="12" height="18.4" rx="2.6"/><path d="M10.6 18.2h2.8"/>',
  key: '<circle cx="7.8" cy="12" r="4.3"/><path d="M12.1 12h8.1M17.6 12v3.4M20.2 12v2.6"/>',
  shield: '<path d="M12 3.4 4.8 6.3v5.5c0 4.4 3 7.6 7.2 8.8 4.2-1.2 7.2-4.4 7.2-8.8V6.3Z"/>',
  // Eight-tooth gear: hub, ring, and spokes drawn as separate strokes rather than one long outline.
  // The usual single-path gear turns to mush at the 16px this renders at next to a text label.
  settings:
    '<circle cx="12" cy="12" r="3.1"/><circle cx="12" cy="12" r="7.6"/><path d="M12 1.9v2.5M12 19.6v2.5M22.1 12h-2.5M4.4 12H1.9M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8M19.1 19.1l-1.8-1.8M6.7 6.7 4.9 4.9"/>',

  clock: '<circle cx="12" cy="12" r="8.2"/><path d="M12 7.1V12l3.2 2.1"/>',
  upload: '<path d="M12 20.2V9.5M7.6 13.9 12 9.5l4.4 4.4M4.5 4.5h15"/>',
  refresh: '<path d="M20 12a8 8 0 1 1-2.6-5.9"/><path d="M20.2 4v4.4h-4.4"/>',
  alert: '<path d="M12 4.4 2.9 19.6h18.2Z"/><path d="M12 10v3.9"/><circle cx="12" cy="16.8" r="1.1" fill="currentColor" stroke="none"/>',
  info: '<circle cx="12" cy="12" r="8.4"/><path d="M12 11.2v5"/><circle cx="12" cy="7.9" r="1.1" fill="currentColor" stroke="none"/>',
  home: '<path d="m3.8 10.8 8.2-6.6 8.2 6.6"/><path d="M6.5 9.4v10.2h11V9.4"/>',
  sparkle: '<path d="m12 3.6 2.1 5.6 5.6 2.1-5.6 2.1L12 19l-2.1-5.6L4.3 11.3l5.6-2.1Z"/>',
};

export interface IconOptions {
  /** Rendered box in px. The stroke is not rescaled, so tiny sizes stay legible. */
  size?: number;
  /** Extra classes on the `<svg>` element. */
  className?: string;
}

/**
 * Icons are decoration next to a visible label, or inside a button that carries its own `aria-label`.
 * Either way the shape itself must not be announced, hence the unconditional `aria-hidden`.
 */
export function iconSvg(name: IconName, options: IconOptions = {}): string {
  const size = options.size ?? 22;
  const cls = options.className ? ` class="${options.className}"` : '';
  return (
    `<svg${cls} width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" ` +
    `stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ` +
    `aria-hidden="true" focusable="false">${ICONS[name]}</svg>`
  );
}

/** For the plain-DOM pages, which build their trees with `createElement` rather than markup. */
export function iconElement(name: IconName, options: IconOptions = {}): SVGSVGElement {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = iconSvg(name, options);
  return wrapper.firstChild as SVGSVGElement;
}

export { ICONS };
